import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import { COMPLIANCE_META, type ComplianceFocus } from '../complianceFocus';
import type {
  CrewStatMixItem,
  CrewStatsCertRow,
  CrewStatsCoeRow,
  CrewStatsMedicalRow,
} from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

type PrintLine = { id: string; cells: string[] };

function TableHead({ headers }: { headers: string[] }) {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-table-head">
      <thead>
        <tr>
          <th className="crew-list-print-col-no">No.</th>
          {headers.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ line, index }: { line: PrintLine; index: number }) {
  return (
    <table className="crew-list-print-table so-flow-table so-flow-row-table">
      <tbody>
        <tr>
          <td className="crew-list-print-col-no">{index + 1}</td>
          {line.cells.map((c, i) => (
            <td key={`${line.id}-${i}`}>{c}</td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function certLines(rows: CrewStatsCertRow[]): { headers: string[]; lines: PrintLine[] } {
  return {
    headers: ['Name', 'Rank', 'Certificate', 'Category', 'Expiry', 'Days'],
    lines: [...rows]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({
        id: String(r.id),
        cells: [
          r.name,
          String(dash(r.rank)),
          String(dash(r.certificate)),
          String(dash(r.category)),
          fmtDate(r.expiredDate),
          r.daysUntilExpiry != null ? String(r.daysUntilExpiry) : '—',
        ],
      })),
  };
}

function medicalLines(rows: CrewStatsMedicalRow[]): { headers: string[]; lines: PrintLine[] } {
  return {
    headers: ['Name', 'Rank', 'Kind', 'Next due', 'Days'],
    lines: [...rows]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({
        id: String(r.id),
        cells: [
          r.name,
          String(dash(r.rank)),
          String(dash(r.kind)),
          fmtDate(r.nextDueDate),
          r.daysUntilExpiry != null ? String(r.daysUntilExpiry) : '—',
        ],
      })),
  };
}

function coeLines(rows: CrewStatsCoeRow[]): { headers: string[]; lines: PrintLine[] } {
  return {
    headers: ['Name', 'Vessel', 'Rank', 'COE', 'Days', 'Months'],
    lines: [...rows]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({
        id: String(r.transactionId),
        cells: [
          r.name,
          String(dash(r.vessel)),
          String(dash(r.rank)),
          fmtDate(r.coeDate),
          r.daysToCoe != null ? String(r.daysToCoe) : '—',
          r.monthsOnboard != null ? String(Math.round(r.monthsOnboard)) : '—',
        ],
      })),
  };
}

function mixLines(rows: CrewStatMixItem[]): { headers: string[]; lines: PrintLine[] } {
  return {
    headers: ['Nationality', 'Onboard'],
    lines: [...rows]
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .map((r) => ({
        id: r.label,
        cells: [r.label, String(r.count)],
      })),
  };
}

export type StatsComplianceReportProps = {
  focus: ComplianceFocus;
  vesselLabel: string;
  withinDays: string;
  certs?: CrewStatsCertRow[];
  medicals?: CrewStatsMedicalRow[];
  coe?: CrewStatsCoeRow[];
  nationalities?: CrewStatMixItem[];
};

export const StatsComplianceReport: React.FC<StatsComplianceReportProps> = ({
  focus,
  vesselLabel,
  withinDays,
  certs = [],
  medicals = [],
  coe = [],
  nationalities = [],
}) => {
  const meta = COMPLIANCE_META[focus];
  const { headers, lines } = useMemo(() => {
    switch (focus) {
      case 'certs-expired':
      case 'certs-expiring':
        return certLines(certs);
      case 'medicals-expired':
      case 'medicals-due':
        return medicalLines(medicals);
      case 'coe-soon':
      case 'intention-overdue':
        return coeLines(coe);
      case 'nationalities':
        return mixLines(nationalities);
    }
  }, [certs, coe, focus, medicals, nationalities]);

  const firstHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <ReportHeader>
          <ReportTitleRow title={meta.title} formId="Compliance" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselLabel} />
              <ReportField label="Within" value={`${withinDays}d`} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead headers={headers} />
      </div>
    ),
    [headers, meta.title, vesselLabel, withinDays],
  );

  const continueHeader = useMemo(
    () => (
      <div className="so-flow-header">
        <div className="so-flow-continue-bar">
          <span className="so-flow-continue-title">{meta.title}</span>
          <span className="so-flow-continue-meta">{vesselLabel}</span>
          <span className="so-flow-continue-hint">continued</span>
        </div>
        <TableHead headers={headers} />
      </div>
    ),
    [headers, meta.title, vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>{meta.body}</ReportNote>
      </ReportFooter>
    ),
    [meta.body],
  );

  const { items, itemKeys } = useMemo(() => {
    if (lines.length === 0) {
      return {
        items: [
          <table key="empty" className="crew-list-print-table so-flow-table">
            <tbody>
              <tr>
                <td colSpan={headers.length + 1} className="crew-list-print-empty">
                  No records for these filters.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: lines.map((line, i) => <LineRow key={line.id} line={line} index={i} />),
      itemKeys: lines.map((l) => l.id),
    };
  }, [headers.length, lines]);

  return (
    <ReportFlowPages
      className="crew-list-print-report crew-list-print-stats-onboard"
      size="portrait"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default StatsComplianceReport;
